import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import ChampionshipsPanel from "@/components/admin/ChampionshipsPanel";
import GalleryPanel from "@/components/admin/GalleryPanel";
import HomePanel from "@/components/admin/HomePanel";
import ApprovalsPanel from "@/components/admin/ApprovalsPanel";
import PromotionsPanel from "@/components/admin/PromotionsPanel";
import UsersPanel from "@/components/admin/UsersPanel";
import AdminEnrollmentsPanel from "@/components/admin/AdminEnrollmentsPanel";
import ProfilePanel from "@/components/admin/ProfilePanel";
import ContactsPanel from "@/components/admin/ContactsPanel";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";
import BackButton from "@/components/BackButton";
import { Trophy, Images, LayoutDashboard, ShieldCheck, CheckSquare, Megaphone, UserCheck, Users, UserCog, Phone, BarChart3 } from "lucide-react";

const TABS = [
  { k: "championships", l: "Ufficiali", icon: Trophy },
  { k: "enrollments", l: "Iscritti", icon: Users },
  { k: "approvals", l: "Approvazioni", icon: CheckSquare },
  { k: "members", l: "Membri", icon: UserCheck },
  { k: "promotions", l: "Promozioni", icon: Megaphone },
  { k: "gallery", l: "Galleria", icon: Images },
  { k: "home", l: "Home", icon: LayoutDashboard },
  { k: "contacts", l: "Contatti", icon: Phone },
  { k: "analytics", l: "Statistiche", icon: BarChart3 },
  { k: "profile", l: "Profilo", icon: UserCog },
];

export default function AdminDashboard() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("championships");

  useEffect(() => {
    if (!loading && !isAdmin) navigate("/accedi");
  }, [loading, isAdmin, navigate]);

  if (loading || user === null) {
    return <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">Caricamento…</div>;
  }
  if (!isAdmin) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <BackButton className="mb-6" />
      <div className="flex items-center gap-3 mb-8">
        <ShieldCheck className="h-8 w-8 text-yellow-400 text-glow-yellow" />
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tighter text-zinc-50">
            Pannello Admin
          </h1>
          <p className="text-sm text-zinc-500">{user.email}</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-zinc-800 mb-8 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-5 py-3 text-xs font-bold tracking-[0.15em] uppercase whitespace-nowrap transition-colors duration-200 border-b-2 -mb-px ${
              tab === t.k ? "border-yellow-400 text-yellow-400" : "border-transparent text-zinc-400 hover:text-zinc-100"
            }`}>
            <t.icon className="h-4 w-4" /> {t.l}
          </button>
        ))}
      </div>

      {tab === "championships" && <ChampionshipsPanel endpoint="/admin/championships" showStatus createKind="official" />}
      {tab === "enrollments" && <AdminEnrollmentsPanel />}
      {tab === "approvals" && <ApprovalsPanel />}
      {tab === "members" && <UsersPanel />}
      {tab === "promotions" && <PromotionsPanel />}
      {tab === "gallery" && <GalleryPanel />}
      {tab === "home" && <HomePanel />}
      {tab === "profile" && <ProfilePanel />}
      {tab === "contacts" && <ContactsPanel />}
      {tab === "analytics" && <AnalyticsPanel />}
    </div>
  );
}
